<template>
  <v-card class="table-card" elevation="0">
    <v-data-table
      :items="reportRows"
      :headers="headers"
      :items-per-page="this.hourly ? 24 : 30"
      hide-default-footer
      class="report-table bordered"
      id="queue-summary-report-table"
      >
      <template v-slot:item.date="{ item }">
        <v-btn v-if="!hourly" text @click="loadHourly(item.date)">{{ dateTemplate(item.date) }}</v-btn>
        <v-btn v-else text @click="loadHourQueueEvents(item.date)">{{ dateTemplate(item.date) }}</v-btn>
      </template>
      <template v-slot:item.calls="{ item }">
        {{ numberTemplate(item.calls) }}
      </template>
      <template v-slot:item.answered="{ item }">
        <v-row v-if="!isMobile" no-gutters>
          <v-col cols="6">{{ numberTemplate(item.answered) }}</v-col>
          <v-col cols="6" class="text--secondary">{{ percentageTemplate(item.answered / item.calls) }}</v-col>
        </v-row>
        <span v-else>
          {{ numberTemplate(item.answered) }}&nbsp;
          <span class="text--secondary">{{ percentageTemplate(item.answered / item.calls) }}</span>
        </span>
      </template>
      <template v-slot:item.abandon="{ item }">
        <v-row v-if="!isMobile" no-gutters>
          <v-col cols="6">{{ numberTemplate(item.abandon) }}</v-col>
          <v-col cols="6" class="text--secondary">{{ percentageTemplate(item.abandon / item.calls) }}</v-col>
        </v-row>
        <span v-else>
          {{ numberTemplate(item.abandon) }}&nbsp;
          <span class="text--secondary">{{ percentageTemplate(item.abandon / item.calls) }}</span>
        </span>
      </template>
      <template v-slot:item.timeout="{ item }">
        <v-row v-if="!isMobile" no-gutters>
          <v-col cols="6">{{ numberTemplate(item.timeout) }}</v-col>
          <v-col cols="6" class="text--secondary">{{ percentageTemplate(item.timeout / item.calls) }}</v-col>
        </v-row>
        <span v-else>
          {{ numberTemplate(item.timeout) }}&nbsp;
          <span class="text--secondary">{{ percentageTemplate(item.timeout / item.calls) }}</span>
        </span>
      </template>
      <template v-slot:item.exit="{ item }">
        {{ numberTemplate(item.exit) }}
      </template>
      <template v-slot:item.avg_hold="{ item }">
        {{ hourTemplate(item.avg_hold) }}
      </template>
      <template v-slot:item.avg_talk="{ item }">
        {{ hourTemplate(item.avg_talk) }}
      </template>
      <template v-slot:item.max_talk="{ item }">
        {{ hourTemplate(item.max_talk) }}
      </template>
      <template v-slot:item.total_talk="{ item }">
        {{ hourTemplate(item.total_talk) }}
      </template>
      <template slot="body.append">
        <tr v-if="isMobile" class="v-data-table__mobile-table-row">
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.total') }}</div>
            <div class="v-data-table__mobile-row__cell"></div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.calls') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ totals.calls }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.answered') }}</div>
            <div class="v-data-table__mobile-row__cell">
              {{ totals.answered }}&nbsp;
              <span class="text--secondary">{{ percentageTemplate(totals.answered / totals.calls) }}</span>
            </div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.abandon') }}</div>
            <div class="v-data-table__mobile-row__cell">
              {{ totals.abandon }}&nbsp;
              <span class="text--secondary">{{ percentageTemplate(totals.abandon / totals.calls) }}</span>
            </div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.timeout') }}</div>
            <div class="v-data-table__mobile-row__cell">
              {{ totals.timeout }}&nbsp;
              <span class="text--secondary">{{ percentageTemplate(totals.timeout / totals.calls) }}</span>
            </div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.exit') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ totals.exit }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.avgHold') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(totals.avg_hold) }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.avgTalk') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(totals.avg_talk) }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.maxTalk') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(totals.max_talk) }}</div>
          </td>
          <td class="v-data-table__mobile-row">
            <div class="v-data-table__mobile-row__header">{{ $t('queues.reports.queueSummary.table.totalTalk') }}</div>
            <div class="v-data-table__mobile-row__cell">{{ hourTemplate(totals.total_talk) }}</div>
          </td>
        </tr>
        <tr v-else class="font-weight-bold totals">
          <td>{{ $t('queues.reports.queueSummary.table.total') }}</td>
          <td class="text-center">{{ totals.calls }}</td>
          <td class="text-center bordered answered">
            <v-row no-gutters>
              <v-col cols="6">{{ totals.answered }}</v-col>
              <v-col cols="6" class="text--secondary">{{ percentageTemplate(totals.answered / totals.calls) }}</v-col>
            </v-row>
          </td>
          <td class="text-center bordered abandoned">
            <v-row no-gutters>
              <v-col cols="6">{{ totals.abandon }}</v-col>
              <v-col cols="6" class="text--secondary">{{ percentageTemplate(totals.abandon / totals.calls) }}</v-col>
            </v-row>
          </td>
          <td class="text-center bordered timeout">
            <v-row no-gutters>
              <v-col cols="6">{{ totals.timeout }}</v-col>
              <v-col cols="6" class="text--secondary">{{ percentageTemplate(totals.timeout / totals.calls) }}</v-col>
            </v-row>
          </td>
          <td class="text-center bordered exit">{{ totals.exit }}</td>
          <td class="text-center">{{ hourTemplate(totals.avg_hold) }}</td>
          <td class="text-center">{{ hourTemplate(totals.avg_talk) }}</td>
          <td class="text-center">{{ hourTemplate(totals.max_talk) }}</td>
          <td class="text-center">{{ hourTemplate(totals.total_talk) }}</td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import formatTime from '@/filters/format_time'
import { formatPercentage } from '@/filters/format_number'

export default {
  name: 'queues-reports-summary-table',
  props: {
    reportRows: {
      type: Array,
      required: true
    },
    totals: {
      type: Object,
      required: true
    },
    selectedQueues: {
      type: Array,
      default: () => []
    },
    table: {
      type: String,
      default: 'queues'
    },
    hourly: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isMobile () {
      return this.$vuetify.breakpoint.mobile
    },
    headers () {
      return [
        this.table === 'queues' ? { text: this.$t('queues.reports.queueSummary.table.queuename'), value: 'queuename' }
          : { text: this.$t('queues.reports.queueSummary.table.date'), value: 'date' },
        { text: this.$t('queues.reports.queueSummary.table.calls'), value: 'calls', align: 'center' },
        { text: this.$t('queues.reports.queueSummary.table.answered'), value: 'answered', align: 'center', class: 'blue--text text--lighten-1 bordered answered', cellClass: 'bordered answered' },
        { text: this.$t('queues.reports.queueSummary.table.abandon'), value: 'abandon', align: 'center', class: 'red--text text--lighten-1 bordered abandoned', cellClass: 'bordered abandoned' },
        { text: this.$t('queues.reports.queueSummary.table.timeout'), value: 'timeout', align: 'center', class: 'grey--text text--darken-1 bordered timeout', cellClass: 'bordered timeout' },
        { text: this.$t('queues.reports.queueSummary.table.exit'), value: 'exit', align: 'center', class: 'blue-grey--text text--lighten-1 bordered exit', cellClass: 'bordered exit' },
        { text: this.$t('queues.reports.queueSummary.table.avgHold'), value: 'avg_hold', align: 'center' },
        { text: this.$t('queues.reports.queueSummary.table.avgTalk'), value: 'avg_talk', align: 'center' },
        { text: this.$t('queues.reports.queueSummary.table.maxTalk'), value: 'max_talk', align: 'center' },
        { text: this.$t('queues.reports.queueSummary.table.totalTalk'), value: 'total_talk', align: 'center' }
      ]
    }
  },
  methods: {
    numberTemplate (value) {
      if (!value) return '-'
      return value
    },
    hourTemplate (seconds) {
      if (!seconds) return '-'
      return formatTime(seconds, true)
    },
    percentageTemplate (value) {
      return formatPercentage(value, 0)
    },
    dateTemplate (value) {
      const date = new Date(value)
      if (this.hourly) {
        return date.getHours().toString().padStart(2, '0') + ':00'
      }
      return (date.getUTCMonth() + 1) + '/' + date.getUTCDate()
    },
    loadHourly (date) {
      this.$emit('refreshData', {
        reportType: 'summary',
        startDate: date + ' 00:00:00',
        endDate: date + ' 23:59:59',
        scope: 'day',
        hourly: true,
        breadcrumb_op: 'add'
      })
    },
    loadHourQueueEvents (date) {
      const endDate = new Date(date + 'Z')
      endDate.setTime(endDate.getTime() + 3_600_000)
      this.$emit('refreshData', {
        reportType: 'callDetails',
        startDate: date,
        endDate: endDate.toISOString().slice(0, 19).replace('T', ' '),
        queue: this.selectedQueues,
        scope: 'hour',
        breadcrumb_op: 'add'
      })
    }
  }
}
</script>
